import * as createPointsOfInterest from 'store/actions/pointsofinterest/createPointsOfInterest';
import * as deletePointsOfInterest from 'store/actions/pointsofinterest/deletePointsOfInterest';
import * as fetchPointsOfInterest from 'store/actions/pointsofinterest/fetchPointsOfInterest';
import * as types from 'store/actions/types';
import * as updatePointsOfInterest from 'store/actions/pointsofinterest/updatePointsOfInterest';
import apiClient from 'clients/apiClient';
import config from 'helpers/config';
import utils from 'helpers/utils';
import { put, takeEvery } from 'redux-saga/effects';
import { showNotification } from 'store/actions/uiActions';

function* fetchPointsOfInterestAsync({ payload }) {
    try {
        // Build the URL.
        const url = `${ config.getApiBaseUrl() }/points-of-interest`;

        // Fetch the data.
        const response = yield apiClient.read(url, payload);

        // Send the data the success action.
        yield put(fetchPointsOfInterest.fetchPointsOfInterestSuccess(response));
    } catch (error) {
        // Trigger the error action.
        yield put(fetchPointsOfInterest.fetchPointsOfInterestFailure(error));
    }
}

function* fetchPointOfInterestAsync({ payload }) {
    const {
        id,
        onError,
    } = payload;

    try {
        // Build the URL.
        const url = `${ config.getApiBaseUrl() }/points-of-interest/${ id }`;

        // Fetch the data.
        const response = yield apiClient.read(url);

        // Send the data to the point of interest.
        yield put(fetchPointsOfInterest.fetchPointOfInterestSuccess(response));

        // Send the data to the success action.
        yield put(fetchPointsOfInterest.fetchPointOfInterestLocalisation(payload));
    } catch (error) {
        // Trigger the error action.
        yield put(fetchPointsOfInterest.fetchPointOfInterestFailure(error));

        if (utils.isFunction(onError)) {
            onError(error);
        }
    }
}

function* fetchPointOfInterestLocalisationAsync({ payload }) {
    const {
        id,
        onError,
        onSuccess,
    } = payload;

    try {
        // Build the URL.
        const url = `${ config.getApiBaseUrl() }/points-of-interest/${ id }/localisation`;

        // Fetch the data.
        const response = yield apiClient.read(url);

        // Send the data to the success action.
        yield put(fetchPointsOfInterest.fetchPointOfInterestLocalisationSuccess(payload, response));

        if (utils.isFunction(onSuccess)) {
            onSuccess();
        }
    } catch (error) {
        // Trigger the error action.
        yield put(fetchPointsOfInterest.fetchPointOfInterestLocalisationFailure(error));

        if (utils.isFunction(onError)) {
            onError(error);
        }
    }
}

function* createPointOfInterestAsync({ payload }) {
    const {
        formValues,
        onError,
    } = payload;

    try {
        // Build the URL.
        const url = `${ config.getApiBaseUrl() }/points-of-interest`;

        // Post the data.
        const response = yield apiClient.create(url, formValues.base);

        yield put(createPointsOfInterest.createPointOfInterestLocalisation(payload, response));
    } catch (error) {
        // Trigger the error action.
        yield put(createPointsOfInterest.createPointOfInterestFailure(error));

        yield onError(error);
    }
}

function* createPointOfInterestImportAsync({ payload }) {
    const {
        formData,
        onError,
        onSuccess,
    } = payload;

    try {
        // Post the data.
        yield apiClient.createFile(`${ config.getApiBaseUrl() }/points-of-interest/import`, formData);

        yield put(showNotification('notifications.points_of_interest.import.success'));

        if (onSuccess) {
            yield onSuccess();
        }
    } catch (error) {
        yield put(createPointsOfInterest.createPointOfInterestImportFailure(error));

        if (onError) {
            yield onError(error);
        }
    }
}

function* createPointOfInterestLocalisationAsync({ payload, response }) {
    const {
        formValues,
        onError,
        onSuccess,
    } = payload;

    const {
        id,
    } = response;

    try {
        // Build the URL.
        const url = `${ config.getApiBaseUrl() }/points-of-interest/${ id }/localisation`;

        if (!utils.isEmpty(formValues.localisation)) {
            yield apiClient.update(url, formValues.localisation);
        }

        yield put(createPointsOfInterest.createPointOfInterestLocalisationSuccess(payload, response));

        yield onSuccess(response);
    } catch (error) {
        // Trigger the error action.
        yield put(createPointsOfInterest.createPointOfInterestLocalisationFailure(error));

        yield onError(error);
    }
}

function* createPointOfInterestLocalisationSuccessAsync() {
    yield put(showNotification('notifications.points_of_interest.create.success'));
}

// Update points of interest.
function* updatePointOfInterestAsync({ payload }) {
    const {
        onError,
        id,
        formValues,
    } = payload;

    try {
        // Build the URL.
        const url = `${ config.getApiBaseUrl() }/points-of-interest/${ id }`;

        // Post the data.
        const response = yield apiClient.patch(url, formValues.base);

        yield put(updatePointsOfInterest.updatePointOfInterestLocalisation(payload, response));
    } catch (error) {
        // Trigger the error action.
        yield put(updatePointsOfInterest.updatePointOfInterestFailure(error));

        yield onError(error);
    }
}

function* updatePointOfInterestLocalisationAsync({ payload, response }) {
    const {
        formValues,
        id,
        onError,
        onSuccess,
    } = payload;

    try {
        // Build the URL.
        const url = `${ config.getApiBaseUrl() }/points-of-interest/${ id }/localisation`;

        if (!utils.isEmpty(formValues.localisation)) {
            yield apiClient.update(url, formValues.localisation);
        }

        yield put(updatePointsOfInterest.updatePointOfInterestLocalisationSuccess(payload, response));

        yield onSuccess();
    } catch (error) {
        // Trigger the error action.
        yield put(updatePointsOfInterest.updatePointOfInterestLocalisationFailure(error));

        yield onError(error);
    }
}

function* updatePointOfInterestLocalisationSuccessAsync() {
    yield put(showNotification('notifications.points_of_interest.update.success'));
}

function* deletePointOfInterestAsync({ payload }) {
    const {
        id,
    } = payload;

    try {
        // Build the URL.
        const url = `${ config.getApiBaseUrl() }/points-of-interest/${ id }`;

        // Delete the data.
        yield apiClient.delete(url);

        yield put(deletePointsOfInterest.deletePointOfInterestSuccess(payload));
    } catch (error) {
        // Trigger the error action.
        yield put(deletePointsOfInterest.deletePointOfInterestFailure(error));
    }
}

function* deletePOISuccessAsync() {
    yield put(showNotification('notifications.points_of_interest.delete.success', 'success'));
}

function* failureMessage({ error }) {
    const { message } = error.response;

    if (!message) {
        return;
    }

    yield put(showNotification(message, 'warning'));
}

export default function* POIsSagas() {
    yield [
        takeEvery(types.FETCH_POINTS_OF_INTEREST, fetchPointsOfInterestAsync),
        takeEvery(types.FETCH_POINTS_OF_INTEREST_FAILURE, failureMessage),

        takeEvery(types.FETCH_POINT_OF_INTEREST, fetchPointOfInterestAsync),
        takeEvery(types.FETCH_POINT_OF_INTEREST_FAILURE, failureMessage),
        takeEvery(types.FETCH_POINT_OF_INTEREST_LOCALISATION, fetchPointOfInterestLocalisationAsync),
        takeEvery(types.FETCH_POINT_OF_INTEREST_LOCALISATION_FAILURE, failureMessage),

        takeEvery(types.CREATE_POINT_OF_INTEREST, createPointOfInterestAsync),
        takeEvery(types.CREATE_POINT_OF_INTEREST_FAILURE, failureMessage),
        takeEvery(types.CREATE_POINT_OF_INTEREST_LOCALISATION, createPointOfInterestLocalisationAsync),
        takeEvery(types.CREATE_POINT_OF_INTEREST_LOCALISATION_FAILURE, failureMessage),
        takeEvery(types.CREATE_POINT_OF_INTEREST_LOCALISATION_SUCCESS, createPointOfInterestLocalisationSuccessAsync),

        takeEvery(types.CREATE_POINT_OF_INTEREST_IMPORT, createPointOfInterestImportAsync),
        takeEvery(types.CREATE_POINT_OF_INTEREST_IMPORT_FAILURE, failureMessage),

        takeEvery(types.UPDATE_POINT_OF_INTEREST, updatePointOfInterestAsync),
        takeEvery(types.UPDATE_POINT_OF_INTEREST_FAILURE, failureMessage),
        takeEvery(types.UPDATE_POINT_OF_INTEREST_LOCALISATION, updatePointOfInterestLocalisationAsync),
        takeEvery(types.UPDATE_POINT_OF_INTEREST_LOCALISATION_FAILURE, failureMessage),
        takeEvery(types.UPDATE_POINT_OF_INTEREST_LOCALISATION_SUCCESS, updatePointOfInterestLocalisationSuccessAsync),

        takeEvery(types.DELETE_POINT_OF_INTEREST, deletePointOfInterestAsync),
        takeEvery(types.DELETE_POINT_OF_INTEREST_FAILURE, failureMessage),
        takeEvery(types.DELETE_POINT_OF_INTEREST_SUCCESS, deletePOISuccessAsync),
    ];
}



// WEBPACK FOOTER //
// ./src/store/sagas/pointOfInterestSagas.js